/**
 * Builds a resolver that maps part names (and their aliases) to the canonical
 * option name from the options library. Use when importing templates or
 * matching presets so that legacy names (e.g. AH4909) are normalized to the
 * current name (e.g. FUS4909LT).
 */
export function buildPartNameResolver(
  options: ReadonlyArray<{ name?: string; aliases?: readonly string[] }>,
): (name: string) => string {
  const map = new Map<string, string>();
  for (const opt of options) {
    const canonical = typeof opt.name === "string" ? opt.name.trim() : "";
    if (!canonical) continue;
    map.set(canonical, canonical);
    if (Array.isArray(opt.aliases)) {
      for (const alias of opt.aliases) {
        if (typeof alias === "string" && alias.trim()) {
          map.set(alias.trim(), canonical);
        }
      }
    }
  }
  return (name: string): string => {
    if (typeof name !== "string" || !name.trim()) return name;
    return map.get(name.trim()) ?? name;
  };
}

/**
 * Resolves partSelection.name in a data object to canonical name when present.
 * Mutates data.partSelection.name in place when a mapping exists.
 */
export function resolvePartSelectionNameInData(
  data: Record<string, unknown>,
  resolvePartName: (name: string) => string,
): void {
  const partSelection = data?.partSelection;
  if (
    !partSelection ||
    typeof partSelection !== "object" ||
    Array.isArray(partSelection)
  ) {
    return;
  }
  const ps = partSelection as Record<string, unknown>;
  const name = ps.name;
  if (typeof name === "string" && name.trim()) {
    const canonical = resolvePartName(name);
    if (canonical !== name) {
      ps.name = canonical;
    }
  }
}

/** Fields that hold option-DB references; stored as { name } only. */
const OPTION_DB_REFERENCE_KEYS = [
  "partSelection",
  "rollerA",
  "rollerB",
  "areaPaddingType",
  "borderPaddingType",
] as const;

/**
 * Strips embedded option-DB copies from preset data down to name-only
 * references.  Templates never store full copies – only `{ name }`.
 * Full objects are resolved from the OptionDB at load time.
 */
export function stripPresetReferences(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...data };

  for (const key of OPTION_DB_REFERENCE_KEYS) {
    const val = result[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const name = (val as { name?: string }).name;
      if (name) {
        result[key] = { name };
      }
    }
  }

  return result;
}
